import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { adminService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Card, Badge, Button, EmptyState, Spinner, Pagination } from '../../components/ui';

const PAGE_SIZE = 20;

function AdminModeration() {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [tab, setTab] = useState('messages');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const fetchReports = () => {
    setLoading(true);
    const fn = tab === 'messages' ? adminService.getMessageReports : adminService.getRatingReports;
    fn({ status: 'pending', page, page_size: PAGE_SIZE })
      .then((res) => {
        const data = res.data;
        setReports(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load reports'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchReports, [tab, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (id, status) => {
    setBusyId(id);
    try {
      const fn = tab === 'messages' ? adminService.resolveMessageReport : adminService.resolveRatingReport;
      await fn(id, { status });
      setReports((prev) => prev.filter((r) => r.id !== id));
      setCount((current) => Math.max(0, current - 1));
      addNotification(t('Report resolved'), 'success');
    } catch (e) {
      addNotification(t('Action failed'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const reportContent = (r) => {
    if (tab === 'messages') {
      return r.group_message ? t('Group message') : t('Private message');
    }
    return t('Rating on module #{{id}}', { id: r.rating });
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Moderation Queue')} subtitle={t('Review reported messages and ratings')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => { setTab('messages'); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'messages' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              {t('Message Reports')}
            </button>
            <button
              onClick={() => { setTab('ratings'); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'ratings' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              {t('Rating Reports')}
            </button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : reports.length === 0 ? (
            <Card><EmptyState title={t('Queue is clear')} description={t('No pending reports right now.')} /></Card>
          ) : (
            <div className="space-y-3">
              {reports.map((r) => (
                <Card key={r.id} className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">{reportContent(r)}</p>
                      <Badge status={r.status} />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{t('Reported by {{name}}', { name: `${r.reported_by?.first_name} ${r.reported_by?.last_name}` })}</p>
                    <p className="text-sm text-gray-500 mt-2 italic">"{r.reason}"</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" loading={busyId === r.id} onClick={() => resolve(r.id, 'dismissed')}>
                      {t('Dismiss')}
                    </Button>
                    <Button size="sm" variant="danger" loading={busyId === r.id} onClick={() => resolve(r.id, 'actioned')}>
                      {t('Take Action')}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
          <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4 rounded-xl border bg-white" />
        </main>
      </div>
    </div>
  );
}

export default AdminModeration;
